import React, { useState, useEffect, useCallback } from 'react';
import ApplicationForm from '../../components/TradeLicense/ApplicationForm';
import ApplicationSuccess from '../../components/TradeLicense/ApplicationSuccess';
import ApplicantLogin from '../../components/TradeLicense/ApplicantLogin';
import ApplicantDashboard from '../../components/TradeLicense/ApplicantDashboard';
import { tradeLicenseApi, applicantAuthApi } from '../../services/tradeLicenseApi';
import './TradeLicense.css';

const TradeLicense = () => {
  const [activeTab, setActiveTab] = useState('apply');
  const [categories, setCategories] = useState([]);
  const [submittedApplication, setSubmittedApplication] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [applicant, setApplicant] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  const checkApplicantAuth = useCallback(async () => {
    setAuthLoading(true);
    try {
      await applicantAuthApi.ensureCsrf();
      const res = await applicantAuthApi.me();
      if (res.data.authenticated) {
        setApplicant(res.data.applicant);
      } else {
        setApplicant(null);
      }
    } catch {
      setApplicant(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    tradeLicenseApi.getCategories()
      .then((res) => setCategories(res.data.results || res.data))
      .catch((err) => console.error('Failed to fetch categories:', err));
    checkApplicantAuth();
  }, [checkApplicantAuth]);

  const handleApplicationSuccess = (data) => {
    setSubmittedApplication(data);
  };

  const handleNewApplication = () => {
    setSubmittedApplication(null);
    setFormKey((k) => k + 1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'apply' && tab !== 'renew') {
      setSubmittedApplication(null);
    }
    if (tab === 'account' && !applicant) {
      setShowLogin(true);
    } else {
      setShowLogin(false);
    }
  };

  const handleLoginSuccess = (data) => {
    setApplicant(data);
    setShowLogin(false);
    setActiveTab('account');
  };

  const handleLogout = async () => {
    try {
      await applicantAuthApi.logout();
    } catch {
      // ignore
    }
    setApplicant(null);
    if (activeTab === 'account') {
      setActiveTab('apply');
    }
  };

  const applicantFormDefaults = applicant ? {
    applicant_phone: applicant.phone,
    applicant_email: applicant.email || '',
    applicant_first_name_en: applicant.first_name || '',
    applicant_last_name_en: applicant.last_name || '',
  } : {};

  return (
    <div className="trade-license-container">
      <div className="tl-header">
        <div className="tl-header-badge">Citizen Services Portal</div>
        <h1>Trade License</h1>
        <p>Apply online, track your application status, and manage licenses — securely and transparently</p>
      </div>

      <div className="tl-tabs">
        <button className={`tab-btn ${activeTab === 'apply' ? 'active' : ''}`} onClick={() => handleTabChange('apply')}>
          📝 Apply for License
        </button>
        <button className={`tab-btn ${activeTab === 'status' ? 'active' : ''}`} onClick={() => handleTabChange('status')}>
          🔍 Check Status
        </button>
        <button className={`tab-btn ${activeTab === 'renew' ? 'active' : ''}`} onClick={() => handleTabChange('renew')}>
          🔄 Renew License
        </button>
        <button className={`tab-btn ${activeTab === 'verify' ? 'active' : ''}`} onClick={() => handleTabChange('verify')}>
          ✔️ Verify License
        </button>
        <button className={`tab-btn ${activeTab === 'account' ? 'active' : ''}`} onClick={() => handleTabChange('account')}>
          {applicant ? '👤 My Account' : '🔐 Login / Sign Up'}
        </button>
      </div>

      <div className="tl-content">
        {activeTab === 'apply' && (
          submittedApplication ? (
            <ApplicationSuccess
              application={submittedApplication}
              onNewApplication={handleNewApplication}
            />
          ) : (
            <ApplicationForm
              key={formKey}
              categories={categories}
              initialData={applicantFormDefaults}
              onSuccess={handleApplicationSuccess}
            />
          )
        )}
        {activeTab === 'status' && <ApplicationStatus />}
        {activeTab === 'renew' && (
          <RenewalForm
            categories={categories}
            applicantDefaults={applicantFormDefaults}
            onSuccess={handleApplicationSuccess}
            submittedApplication={submittedApplication}
            onNewApplication={handleNewApplication}
          />
        )}
        {activeTab === 'verify' && <LicenseVerification />}
        {activeTab === 'account' && (
          authLoading ? (
            <div className="loading">Checking login…</div>
          ) : applicant && !showLogin ? (
            <ApplicantDashboard
              applicant={applicant}
              categories={categories}
              onLogout={handleLogout}
            />
          ) : (
            <ApplicantLogin
              onSuccess={handleLoginSuccess}
              onCancel={() => setActiveTab('apply')}
            />
          )
        )}
      </div>
    </div>
  );
};

const ApplicationStatus = () => {
  const [applicationNumber, setApplicationNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await tradeLicenseApi.publicStatus({
        application_number: applicationNumber,
        phone,
      });
      setApplication(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Application not found');
      setApplication(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tl-status-container">
      <div className="form-header">
        <h2>Check Application Status</h2>
        <p>Enter application number and registered mobile number (no login required)</p>
      </div>
      <form onSubmit={handleSearch} className="status-search">
        <input
          type="text"
          value={applicationNumber}
          onChange={(e) => setApplicationNumber(e.target.value)}
          placeholder="Application number (e.g., TL-20250607-1234)"
          required
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Registered mobile number"
          required
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>
      {error && <div className="alert alert-error">{error}</div>}
      {application && (
        <div className="status-result">
          <div className="status-card">
            <h3>{application.business_name_en}</h3>
            <dl className="status-details">
              <dt>Application #:</dt><dd>{application.application_number}</dd>
              <dt>Applicant:</dt><dd>{application.applicant_name}</dd>
              <dt>Status:</dt>
              <dd><span className={`status-badge ${application.status}`}>{application.status}</span></dd>
              <dt>Stage:</dt><dd>{application.current_stage_name}</dd>
              <dt>Submitted:</dt>
              <dd>{application.submitted_date ? new Date(application.submitted_date).toLocaleDateString() : '—'}</dd>
            </dl>
            {application.status === 'issued' && application.license_number && (
              <div style={{ marginTop: '16px' }}>
                <a href={`/trade-license/certificate/${application.license_number}`} target="_blank" rel="noreferrer" className="btn btn-primary">
                  📜 View License Certificate
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RenewalForm = ({ categories, applicantDefaults, onSuccess, submittedApplication, onNewApplication }) => {
  const [licenseNumber, setLicenseNumber] = useState('');
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [renewMode, setRenewMode] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [renewSubmitting, setRenewSubmitting] = useState(false);

  const handleRenewAsIs = async () => {
    if (!license) return;
    const ok = window.confirm(
      `Renew license ${license.license_number} with the same details?\n` +
      'Your renewal application will be submitted directly — no form to fill.'
    );
    if (!ok) return;
    setRenewSubmitting(true);
    setError(null);
    try {
      const response = await tradeLicenseApi.renewAsIs(license.license_number);
      onSuccess?.(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit renewal. Please try again.');
    } finally {
      setRenewSubmitting(false);
    }
  };

  if (submittedApplication) {
    return (
      <ApplicationSuccess
        application={submittedApplication}
        onNewApplication={() => {
          onNewApplication?.();
          setLicense(null);
          setRenewMode(null);
          setLicenseNumber('');
          setFormKey((k) => k + 1);
        }}
      />
    );
  }

  if (renewMode === 'modify' && license?.prefill) {
    return (
      <div>
        <div className="renewal-mode-banner">
          <p>Modify details as needed before submitting renewal.</p>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => { setRenewMode(null); setFormKey((k) => k + 1); }}
          >
            ← Back to license search
          </button>
        </div>
        <ApplicationForm
          key={`modify-${formKey}`}
          categories={categories}
          initialData={{ ...applicantDefaults, ...license.prefill }}
          lockApplicationType
          readOnlyFields={['existing_license_number']}
          onSuccess={onSuccess}
        />
      </div>
    );
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setLicense(null);
    try {
      const response = await tradeLicenseApi.lookupLicense(licenseNumber);
      if (!response.data.is_active) {
        setError('License found but is not currently active. Please contact the municipal office.');
      } else {
        setLicense(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'License not found. Please check the license number.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tl-form-container tl-form-wide">
      <div className="form-header">
        <h2>Renew Your License</h2>
        <p>Search your existing license number to renew or update details</p>
      </div>

      <form onSubmit={handleSearch} className="status-search">
        <input
          type="text"
          value={licenseNumber}
          onChange={(e) => setLicenseNumber(e.target.value)}
          placeholder="Enter existing license number (e.g., LIC-20250101-1234)"
          required
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search License'}
        </button>
      </form>

      {error && <div className="alert alert-error">{error}</div>}

      {license && (
        <div className="status-result">
          <div className="license-info">
            <h3>✓ License Found</h3>
            <dl className="status-details">
              <dt>License #:</dt><dd><strong>{license.license_number}</strong></dd>
              <dt>Business Name:</dt><dd>{license.business_name}</dd>
              <dt>Owner:</dt><dd>{license.owner_name}</dd>
              <dt>Valid From:</dt><dd>{new Date(license.valid_from).toLocaleDateString()}</dd>
              <dt>Valid Till:</dt><dd>{new Date(license.valid_till).toLocaleDateString()}</dd>
              <dt>Status:</dt>
              <dd><span className="status-badge issued">{license.status.toUpperCase()}</span></dd>
              <dt>Address:</dt><dd>{license.business_address}</dd>
            </dl>

            <div className="renewal-options">
              <h4>How would you like to proceed?</h4>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRenewAsIs}
                disabled={!license.prefill || renewSubmitting}
              >
                {renewSubmitting ? 'Submitting…' : '🔄 Renew with Same Details (One Click)'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRenewMode('modify')}
                disabled={!license.prefill || renewSubmitting}
              >
                ✏️ Renew with Modifications
              </button>
              <p className="section-hint">
                "Same Details" reuses your previous application, photograph and documents —
                the renewal is submitted instantly without filling any form.
              </p>
            </div>
            {!license.prefill && (
              <p className="section-hint">License details could not be pre-filled. Please contact the office for renewal.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LicenseVerification = () => {
  const [licenseNumber, setLicenseNumber] = useState('');
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await tradeLicenseApi.lookupLicense(licenseNumber);
      if (!response.data.is_active) {
        setError('License found but is not currently active.');
        setLicense(null);
      } else {
        setLicense(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'License not found.');
      setLicense(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tl-verify-container">
      <div className="form-header">
        <h2>Verify Trade License</h2>
        <p>Search and verify any active trade license</p>
      </div>
      <form onSubmit={handleVerify} className="verify-form">
        <input
          type="text"
          value={licenseNumber}
          onChange={(e) => setLicenseNumber(e.target.value)}
          placeholder="Enter license number"
          required
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Verifying…' : 'Verify'}
        </button>
      </form>
      {error && <div className="alert alert-error">{error}</div>}
      {license && (
        <div className="status-result">
          <div className="license-info">
            <h3>✓ License Verified</h3>
            <dl className="status-details">
              <dt>License #:</dt><dd><strong>{license.license_number}</strong></dd>
              <dt>Business Name:</dt><dd>{license.business_name}</dd>
              <dt>Owner:</dt><dd>{license.owner_name}</dd>
              <dt>Valid From:</dt><dd>{new Date(license.valid_from).toLocaleDateString()}</dd>
              <dt>Valid Till:</dt><dd>{new Date(license.valid_till).toLocaleDateString()}</dd>
              <dt>Status:</dt>
              <dd><span className="status-badge issued">{license.status.toUpperCase()}</span></dd>
              <dt>Address:</dt><dd>{license.business_address}</dd>
            </dl>
            <div style={{ marginTop: '20px' }}>
              <a href={`/trade-license/certificate/${license.license_number}`} target="_blank" rel="noreferrer" className="btn btn-primary">📜 View Full Certificate</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeLicense;
